import { useCallback, useEffect, useId, useRef, useState } from 'react'
import type { RangeUser } from '~/lib/api/types'
import { useCalendarFilters } from './use-filters'
import styles from './filters.module.scss'

type Props = {
  users: ReadonlyArray<RangeUser>
}

export function FiltersControl({ users }: Props) {
  const {
    filters,
    toggleEngineer,
    setOooOnly,
    setParseFailedOnly,
    clearAll,
    activeCount,
  } = useCalendarFilters()

  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelId = useId()

  const close = useCallback(() => {
    setOpen(false)
    triggerRef.current?.focus()
  }, [])

  useEffect(() => {
    if (!open) return undefined
    const onPointerDown = (e: MouseEvent) => {
      if (!rootRef.current) return
      if (!rootRef.current.contains(e.target as Node)) close()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, close])

  const sorted = [...users].sort((a, b) =>
    (a.display_name ?? a.slack_user_id).localeCompare(
      b.display_name ?? b.slack_user_id
    )
  )

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={`${styles.button} ${activeCount > 0 ? styles.buttonActive : ''}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={panelId}
        onClick={() => setOpen(v => !v)}
      >
        Filter
        {activeCount > 0 && (
          <span
            className={styles.badge}
            aria-label={`${activeCount} filters active`}
          >
            {activeCount}
          </span>
        )}
        <span aria-hidden>▾</span>
      </button>

      {open && (
        <div
          id={panelId}
          role="dialog"
          aria-label="Calendar filters"
          className={styles.panel}
        >
          <div className={styles.section}>
            <div className={styles.sectionHead}>
              <span className={styles.sectionTitle}>Show only</span>
            </div>
            <label className={styles.toggle}>
              <input
                type="checkbox"
                checked={filters.oooOnly}
                onChange={e => setOooOnly(e.target.checked)}
              />
              <span>OOO only</span>
            </label>
            <label className={styles.toggle}>
              <input
                type="checkbox"
                checked={filters.parseFailedOnly}
                onChange={e => setParseFailedOnly(e.target.checked)}
              />
              <span>Parse failures only</span>
            </label>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionHead}>
              <span className={styles.sectionTitle}>Engineers</span>
              <span className={styles.sectionHint}>
                {filters.engineers.size === 0
                  ? 'all'
                  : `${filters.engineers.size} of ${users.length}`}
              </span>
            </div>
            {sorted.length === 0 ? (
              <p className={styles.empty}>No engineers in range.</p>
            ) : (
              <ul className={styles.engineerList}>
                {sorted.map(u => {
                  const checked = filters.engineers.has(u.slack_user_id)
                  return (
                    <li key={u.id}>
                      <label className={styles.toggle}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleEngineer(u.slack_user_id)}
                        />
                        <span>{u.display_name ?? u.slack_user_id}</span>
                      </label>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>

          <div className={styles.footer}>
            <button
              type="button"
              className={styles.clearButton}
              onClick={clearAll}
              disabled={activeCount === 0}
            >
              Clear all
            </button>
            <button type="button" className={styles.doneButton} onClick={close}>
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
